/**
 * Alderperson's office widgets (screenshots/73 "Alderperson's offices"; 70): Your ward said, Whip count, Constituents who
 * spoke, Lobbying on your items, Meeting log, and Allies & opponents (office default only). Ward totals are counts of
 * verified residents' positions, never identities (§6 ticket 17).
 */
import { OvIcon } from "../icons";
import { OvChip, Overflow, ProjBar, Row, RowFace, Stat, fit } from "./parts";
import type { WidgetProps } from "./props";

const pct = (a: number, b: number) => (a + b ? Math.round((a / (a + b)) * 100) : 0);

/** S: "561 · How would you vote? answers, verified addresses"; M/L: support vs. oppose per item. */
export function WardSaid({ feeds, size, h }: WidgetProps) {
  const f = feeds.wardSaid;
  if (!f) return null;
  if (size === "S") return <Stat value={f.total} sub="How would you vote? answers, verified addresses" />;
  const shown = f.items.slice(0, fit(h, 82, 24));
  return <WardBars items={shown} more={f.items.length - shown.length} />;
}

/** The support/oppose bars (70 "Chart · Your ward said"): legend, then one split bar per item. */
export function WardBars({ items, more = 0 }: { items: { recordId: string; title: string; support: number; oppose: number }[]; more?: number }) {
  return (
    <div className="ov-ward">
      <div className="ov-legend"><span className="ov-legend__sw ov-legend__sw--support" />Support<span className="ov-legend__sw ov-legend__sw--oppose" />Oppose</div>
      {items.map((i) => {
        const p = pct(i.support, i.oppose);
        return (
          <div key={i.recordId} className="ov-ward__item">
            <div className="ov-ward__head"><span>{i.title}</span><span className={p >= 50 ? "is-support" : "is-oppose"}>{p}% support · {i.support + i.oppose}</span></div>
            <div className="ov-ward__bar" role="img" aria-label={`${i.support} support, ${i.oppose} oppose`}>
              <span className="ov-ward__s" style={{ flexGrow: i.support }} /><span className="ov-ward__o" style={{ flexGrow: i.oppose }} />
            </div>
          </div>
        );
      })}
      <Overflow n={more} />
    </div>
  );
}

export function WhipCount({ feeds, h }: WidgetProps) {
  const f = feeds.whipCount;
  if (!f) return null;
  const shown = f.members.slice(0, fit(h, 60, 52));
  return (
    <div className="ov-whip">
      <div className="ov-whip__proj">
        <ProjBar proj={f.proj} width="100%" height={10} />
        <div className="ov-whip__legend"><span>Likely yes {f.proj.yes}</span><span>Toss-up {f.proj.tossup}</span><span>Likely no {f.proj.no}</span></div>
      </div>
      <div className="ov-rows ov-rows--spread">
        {shown.map((m) => (
          <Row key={m.personId} lead={<RowFace name={m.name} photoUrl={m.photoUrl} size={28} />} title={m.name} sub={`Ward ${m.ward} · ${Math.round(m.pct * 100)}% yes`}
            right={m.phone ? <a className="ov-call" href={`tel:${m.phone.replace(/[^\d+]/g, "")}`} aria-label={`Call ${m.name}`}><OvIcon name="phone" size={13} />Call</a> : undefined} />
        ))}
      </div>
      <Overflow n={f.members.length - shown.length} />
    </div>
  );
}

export function ConstituentsSpoke({ feeds }: WidgetProps) {
  const f = feeds.constituentsSpoke;
  if (!f) return null;
  return <Stat value={f.count} chip={{ text: `Ward ${f.ward}`, tone: "neutral" }} sub={f.since} />;
}

/** "Table · Lobbying on your items" (70): WHO · ITEM · STANCE. Columns drop right-to-left when smaller (71). */
export function LobbyingOnItems({ feeds, h, w }: WidgetProps) {
  const rows = feeds.lobbyingOnItems?.rows ?? [];
  const shown = rows.slice(0, fit(h, 42, 26));
  const cols = w >= 6 ? 3 : w >= 4 ? 2 : 1;
  const STANCE = { supports: ["Supports", "is-support"], opposes: ["Opposes", "is-oppose"], unclear: ["Position unclear", ""], none: ["No one yet", ""] } as const;
  return (
    <table className="ov-table">
      <thead><tr><th>Who</th>{cols > 1 && <th className="ov-table__item">Item</th>}{cols > 2 && <th className="ov-table__stance">Stance</th>}</tr></thead>
      <tbody>
        {shown.map((r, n) => (
          <tr key={n}>
            <td className="ov-table__strong">{r.who || "—"}</td>
            {cols > 1 && <td className="ov-table__muted">{r.item}</td>}
            {cols > 2 && <td className={STANCE[r.stance][1] ? `ov-table__stance-v ${STANCE[r.stance][1]}` : "ov-table__muted"}>{STANCE[r.stance][0]}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function MeetingLog({ feeds, h }: WidgetProps) {
  const es = feeds.meetingLog?.entries ?? [];
  const shown = es.slice(0, fit(h, 50));
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((e, n) => <Row key={n} href={e.href} lead={<span className="ov-row__date">{e.date}</span>} title={e.text} />)}
      <Overflow n={es.length - shown.length} />
    </div>
  );
}

export function Allies({ feeds, h }: WidgetProps) {
  const ms = feeds.allies?.members ?? [];
  const shown = ms.slice(0, fit(h, 60));
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((m) => (
        <Row key={m.personId} lead={<RowFace name={m.name} photoUrl={m.photoUrl} size={28} />} title={m.name} href={`/dashboard/people/${m.personId}`}
          right={<span className={`ov-row__value ${m.pct >= 0.5 ? "is-support" : "is-oppose"}`}>{Math.round(m.pct * 100)}%</span>} />
      ))}
      <Overflow n={ms.length - shown.length} />
    </div>
  );
}

export { OvChip };
